import React, { Suspense, useState } from 'react';
import { MemoryRouter } from 'react-router';
import { ThemeProvider } from '@emotion/react';
import { useMount } from 'react-use';
import { observer } from 'mobx-react';
import 'dayjs/locale/de';
import {
  ExternalSearchParams,
  ExternalSearchParamsContextProps,
  ExternalFormatPrice,
  LoadingOverlay,
  Overlay
} from '@ibe/components';
import theme from '../Theme/theme';
import { boot } from '../Config/config';
import initI18next from '../Translations/setup';
import { ConfigModel, LoggerFactory, formatPrice } from '@ibe/services';
import { ThgConfigModel } from '../Config/ThgConfigModel';
import '../Theme/Styles/widgets.scss';
import ProductIBE from '../Pages/Products/ProductIBE';
import { ProductIBEProps } from '../Pages/Products/PackagesList';
import { extendedConfigContext } from '../Config/extendedConfigContext';

const logger = LoggerFactory.get('ProductWidget');

const ProductWidget = observer(
  (
    props: ExternalSearchParamsContextProps & {
      thgConfig: ThgConfigModel;
      configOverrides?: ConfigModel;
      basePrice: string;
      seasonBestPriceDate?: string;
      seasonBestPricePackageCode?: string;
      suppressStrikePrices?: boolean;
    } & ProductIBEProps
  ) => {
    const {
      widgetSessionKeyPrefix: keyPrefix,
      thgConfig,
      configOverrides,
      earliestPossibleDate,
      latestPossibleDate,
      packageCodes,
      checkoutUrl,
      basePrice,
      seasonBestPriceDate,
      seasonBestPricePackageCode,
      mode,
      suppressStrikePrices
    } = props;
    const [bootstrappingFinished, setBootstrappingFinished] = useState<boolean>(false);

    const widgetSessionKeyPrefix = keyPrefix || document.body.id || '';

    useMount(async () => {
      try {
        await initI18next(
          thgConfig.baseUrl,
          thgConfig.version,
          configOverrides?.tenant ?? undefined,
          mode
        );
        await boot(false, widgetSessionKeyPrefix, configOverrides, true, thgConfig);
      } catch (err) {
        logger.error(err);
      } finally {
        setBootstrappingFinished(true);
        const event = new CustomEvent('productIbeWidgetReady');
        window.dispatchEvent(event);
      }
    });

    return bootstrappingFinished ? (
      <ThemeProvider theme={theme}>
        <div style={{ backgroundColor: 'transparent' }} id="iso">
          <Suspense fallback={<Overlay fullOpacity />}>
            <extendedConfigContext.Provider value={thgConfig}>
              <MemoryRouter>
                <ExternalSearchParams {...props} widgetSessionKeyPrefix={widgetSessionKeyPrefix}>
                  <LoadingOverlay isLoading={!bootstrappingFinished}>
                    <ExternalFormatPrice externalFormatPrice={formatPrice}>
                      <ProductIBE
                        earliestPossibleDate={earliestPossibleDate}
                        latestPossibleDate={latestPossibleDate}
                        seasonBestPriceDate={seasonBestPriceDate}
                        seasonBestPricePackageCode={seasonBestPricePackageCode}
                        packageCodes={packageCodes}
                        checkoutUrl={checkoutUrl}
                        maintenanceMode={thgConfig.maintenanceMode}
                        basePrice={basePrice}
                        suppressStrikePrices={suppressStrikePrices}
                      />
                    </ExternalFormatPrice>
                  </LoadingOverlay>
                </ExternalSearchParams>
              </MemoryRouter>
            </extendedConfigContext.Provider>
          </Suspense>
        </div>
      </ThemeProvider>
    ) : (
      <div id="iso" style={{ height: '300px' }}>
        <Overlay />
      </div>
    );
  }
);

export default ProductWidget;
